import type { SortingState } from "@tanstack/react-table";
import {
  FC,
  Fragment,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import { inputString, totalModelFallbacks } from "@tsmono/inspect-common/utils";
import type {
  ColumnFilter,
  FilterSpec,
  FilterType,
} from "@tsmono/inspect-components/columnFilter";
import {
  ErrorPanel,
  NoContentsPanel,
  ToolButton,
} from "@tsmono/react/components";

import { EvalLogStatus } from "../../../@types/extraInspect.ts";
import { getApi } from "../../../app_config";
import { InlineSampleDisplay } from "../../../app/samples/InlineSampleDisplay.tsx";
import { SampleList } from "../../../app/samples/list/SampleList.tsx";
import { sampleSelectColumn } from "../../../app/samples/list/SampleSelectColumn.tsx";
import { parseFilterSpecs } from "../../../app/samples/sample-tools/astToSpecs.ts";
import { buildSampleFilterSpecRegistry } from "../../../app/samples/sample-tools/filterSpecRegistry.ts";
import { specsToFilterText } from "../../../app/samples/sample-tools/specsToFilterText.ts";
import {
  SampleTools,
  ScoreFilterTools,
} from "../../../app/samples/SamplesTools.tsx";
import {
  totalSampleCost,
  totalSampleTokens,
} from "../../../client/utils/derive.ts";
import { kLogViewSamplesTabId } from "../../../constants.ts";
import {
  useEffectiveEvalConfig,
  useFilteredSamples,
  useSampleDescriptor,
  useScores,
  useSelectedLogDetails,
  useSelectedSampleSummaries,
  useSelectedScores,
  useTotalSampleCount,
} from "../../../state/hooks.ts";
import { useStore } from "../../../state/store.ts";
import { NavbarButton } from "../../navbar/NavbarButton.tsx";
import { useCurrentLogFile } from "../../routing/currentSelection";
import {
  sortingToViewSort,
  viewSortToSorting,
} from "../../samples/list/samplesView.converters.ts";
import {
  useSamplesView,
  useSamplesViewColorScalesEnabled,
  useSamplesViewCompactScores,
  useSamplesViewMultiline,
  useSamplesViewScoreColorScales,
  useSamplesViewScoreLabels,
} from "../../samples/list/useSamplesView.ts";
import { ColumnSelectorPopover } from "../../shared/ColumnSelectorPopover.tsx";
import { ExtendedColumnDef } from "../../shared/data-grid/columnTypes.ts";
import { type PickerColumn } from "../../shared/gridUtils.ts";
import { type WireScoreColorScale } from "../../shared/samples-grid/colorScale.ts";
import {
  buildSampleColumns,
  perScorerFieldKey,
} from "../../shared/samples-grid/columns.tsx";
import { SampleRow } from "../../shared/samples-grid/types.ts";

import { RunningNoSamples } from "./RunningNoSamples.tsx";

interface SamplesTabExtraProps {
  showColumnSelector: boolean;
  setShowColumnSelector: (showing: boolean) => void;
  columnButtonEl: HTMLButtonElement | null;
}

// Stable empty fallback: a fresh `{}` per render would invalidate the
// `allColumns` memo every render (and tear down score-cell DOM).
const kNoScoreColorScales: Record<string, WireScoreColorScale> = Object.freeze(
  {}
);

// Stable empty fallback: a fresh `{}` per render (while the representability
// gate is active) would defeat SampleList's memo.
const kNoColumnFilters: Record<string, ColumnFilter> = Object.freeze({});

// Stable empty fallback for logs with no persisted column widths.
const kNoColumnSizing: Record<string, number> = Object.freeze({});

// Picker-column shim of the column list for `useSamplesView` /
// `ColumnSelectorPopover`, which key off `colId` / `headerName`.
const toPickerColumns = (
  columns: ExtendedColumnDef<SampleRow>[]
): PickerColumn[] =>
  columns.map((col) => ({
    colId: col.id,
    headerName: typeof col.header === "string" ? col.header : "",
  }));

// Individual hook for Samples tab
export const useSamplesTabConfig = (
  evalStatus: EvalLogStatus | undefined,
  refreshLog: () => void
) => {
  const totalSampleCount = useTotalSampleCount();
  const samplesDescriptor = useSampleDescriptor();
  const streamSamples = useStore((state) => state.capabilities.streamSamples);

  // Single ref exposed to the parent so cross-tab UI (title-view
  // collapse-on-scroll) can listen to scrolling inside this tab. Only one of
  // `InlineSampleDisplay` or `SampleList` is mounted at a time, so they
  // share this ref.
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Column-selector state lives here so the tools toolbar can host the
  // trigger button while the popover is rendered inside SamplesTab.
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [columnButtonEl, setColumnButtonEl] =
    useState<HTMLButtonElement | null>(null);
  const handleToggleColumnSelector = useCallback(() => {
    setShowColumnSelector((p) => !p);
  }, []);

  return useMemo(() => {
    const samplesAvailable =
      samplesDescriptor !== undefined && totalSampleCount > 1;
    return {
      id: kLogViewSamplesTabId,
      scrollable: false,
      scrollRef,
      label: totalSampleCount > 1 ? "Samples" : "Sample",
      component: SamplesTab,
      componentProps: {
        running: evalStatus === "started",
        scrollRef,
        showColumnSelector,
        setShowColumnSelector,
        columnButtonEl,
      },
      tools: () =>
        !samplesDescriptor
          ? undefined
          : totalSampleCount === 1
            ? [<ScoreFilterTools key="sample-score-tool" />]
            : [
                <SampleTools key="sample-tools" />,
                evalStatus === "started" && !streamSamples && (
                  <ToolButton
                    key="refresh"
                    label="Refresh"
                    icon="refresh"
                    subtle
                    onClick={refreshLog}
                  />
                ),
                samplesAvailable && (
                  <NavbarButton
                    key="choose-columns"
                    ref={setColumnButtonEl}
                    label="Columns"
                    icon="columns"
                    dropdown
                    subtle
                    onClick={handleToggleColumnSelector}
                  />
                ),
              ],
    };
    // `setShowColumnSelector` is intentionally omitted — React state setters
    // have stable identity across renders, so including it only churns the
    // memo without changing behavior.
  }, [
    evalStatus,
    refreshLog,
    samplesDescriptor,
    streamSamples,
    totalSampleCount,
    showColumnSelector,
    columnButtonEl,
    handleToggleColumnSelector,
  ]);
};

interface SamplesTabProps extends SamplesTabExtraProps {
  running: boolean;
  scrollRef?: RefObject<HTMLDivElement | null>;
}

export const SamplesTab: FC<SamplesTabProps> = ({
  running,
  scrollRef,
  showColumnSelector,
  setShowColumnSelector,
  columnButtonEl,
}) => {
  const sampleSummaries = useFilteredSamples();
  // The summaries' loading/error surface — the derivation hooks above all
  // compute over the settled rows this AsyncData carries.
  const summariesState = useSelectedSampleSummaries();
  const selectedLogDetails = useSelectedLogDetails();
  const selectedLogFile = useCurrentLogFile();

  // Effective (folded) config — limit/epochs are launch-shaped, but routing
  // every config read through the fold makes that assumption enforced.
  const effectiveConfig = useEffectiveEvalConfig();
  const evalSampleCount = useMemo(() => {
    const limit = effectiveConfig?.limit;
    const limitCount =
      limit === null || limit === undefined
        ? undefined
        : typeof limit === "number"
          ? limit
          : limit[1] - limit[0];
    return (
      (limitCount || selectedLogDetails?.eval.dataset.samples || 0) *
      (effectiveConfig?.epochs || 0)
    );
  }, [
    effectiveConfig?.epochs,
    effectiveConfig?.limit,
    selectedLogDetails?.eval.dataset.samples,
  ]);

  const totalSampleCount = useTotalSampleCount();

  const samplesDescriptor = useSampleDescriptor();
  const selectedScores = useSelectedScores();
  const scores = useScores();
  const setSelectedScores = useStore(
    (state) => state.logActions.setSelectedScores
  );
  const epochs = effectiveConfig?.epochs || 1;

  // Build the superset of available columns once. Score columns are
  // emitted for every available score; visibility (which scorers are
  // currently selected) is applied via the column-visibility map below.
  // Multiline determines column rendering: list-style uses
  // MarkdownCellDiv (3-line clamp) which doesn't center in 30px rows.
  const multiline = useSamplesViewMultiline();
  const compactScores = useSamplesViewCompactScores();
  const scoreLabels = useSamplesViewScoreLabels();
  const wireScoreColorScales = useSamplesViewScoreColorScales();
  const colorScalesEnabled = useSamplesViewColorScalesEnabled();
  // Gate the heat-map colours behind the user-facing toggle: pass the
  // resolved scales through only when enabled, else nothing.
  const scoreColorScales = useMemo(
    () => (colorScalesEnabled ? wireScoreColorScales : kNoScoreColorScales),
    [colorScalesEnabled, wireScoreColorScales]
  );

  const filter = useStore((state) => state.log.filter);
  const setFilter = useStore((state) => state.logActions.setFilter);

  const filterSpecRegistry = useMemo(
    () => buildSampleFilterSpecRegistry(samplesDescriptor?.evalDescriptor),
    [samplesDescriptor?.evalDescriptor]
  );

  // Column funnels are a projection of the FILTER string: derive their state
  // by parsing it, and hide them entirely when the expression isn't
  // representable as per-column filters (so a funnel edit can't clobber a
  // richer hand-typed expression).
  const columnFilterSpecs = useMemo(
    () => parseFilterSpecs(filter, filterSpecRegistry),
    [filter, filterSpecRegistry]
  );

  const handleColumnFilterChange = useCallback(
    (columnId: string, filterType: FilterType, spec: FilterSpec | null) => {
      if (columnFilterSpecs === null) return;
      const next = { ...columnFilterSpecs };
      if (spec === null) delete next[columnId];
      else next[columnId] = { columnId, filterType, spec };
      const text = specsToFilterText(next, filterSpecRegistry);
      if (text !== null) setFilter(text);
    },
    [columnFilterSpecs, filterSpecRegistry, setFilter]
  );

  const allColumns = useMemo(
    () =>
      buildSampleColumns({
        viewMode: multiline ? "list" : "grid",
        multiLog: false,
        descriptor: samplesDescriptor,
        scores,
        epochs,
        scoreLabels,
        scoreColorScales,
        compactScores,
        filterSpecRegistry,
      }),
    [
      multiline,
      samplesDescriptor,
      scores,
      epochs,
      scoreLabels,
      scoreColorScales,
      compactScores,
      filterSpecRegistry,
    ]
  );

  const pickerColumns = useMemo(
    () => toPickerColumns(allColumns),
    [allColumns]
  );

  // Default visibility for unseeded columns. Core text columns
  // (input/target/answer) are visible by default — the user can hide
  // them if not useful. limit/retries/error default off and only
  // auto-promote to visible when data is present, mirroring the
  // SamplesPanel behavior.
  const shape = samplesDescriptor?.messageShape;
  const defaultsForUnseededColumns = useCallback(
    (col: PickerColumn) => {
      const id = col.colId;
      if (id === "epoch") return epochs > 1;
      if (id === "limit") return !!shape?.limitSize;
      if (id === "retries") return !!shape?.retriesSize;
      if (id === "fallbacks") return !!shape?.fallbacksSize;
      if (id === "error") return !!shape?.errorSize;
      if (id === "sampleUuid") return false;
      if (id === "cost") return false;
      return true;
    },
    [shape, epochs]
  );

  const {
    view,
    columnVisibility,
    setColumnVisibility,
    patchView,
    resetColumns,
  } = useSamplesView(pickerColumns, {
    seedDefaultVisibility: defaultsForUnseededColumns,
  });

  // Apply the eval-author's initial column order + row sort from
  // `task_samples_view` (the AG grid did this via `initialState`). Columns are
  // reordered to follow `view.columns`; any not listed keep their builder
  // order at the end. Default sort seeds the grid from `view.sort`.
  // The checkbox column stays out of `allColumns` so the column picker and
  // the persisted view never see it.
  const api = getApi();
  const canExportSamples =
    api.download_log_subset !== undefined || api.save_log_subset !== undefined;
  const orderedColumns = useMemo<ExtendedColumnDef<SampleRow>[]>(() => {
    const selectColumns = canExportSamples ? [sampleSelectColumn] : [];
    if (view.columns.length === 0) return [...selectColumns, ...allColumns];
    const orderIndex = new Map(view.columns.map((c, i) => [c.id, i]));
    const rankOf = (col: ExtendedColumnDef<SampleRow>) => {
      const i = col.id !== undefined ? orderIndex.get(col.id) : undefined;
      return i ?? Number.MAX_SAFE_INTEGER;
    };
    return [
      ...selectColumns,
      ...allColumns
        .map((col, i) => ({ col, i }))
        .sort((a, b) => rankOf(a.col) - rankOf(b.col) || a.i - b.i)
        .map((x) => x.col),
    ];
  }, [allColumns, view.columns, canExportSamples]);

  // Controlled sort/widths, persisted per log in the samples-view descriptor
  // so they survive the grid unmounting (opening a sample, switching tabs).
  const sorting = useMemo<SortingState>(
    () => viewSortToSorting(view.sort),
    [view.sort]
  );
  const handleSortingChange = useCallback(
    (next: SortingState) => patchView({ sort: sortingToViewSort(next) }),
    [patchView]
  );
  const columnSizing = view.columnWidths ?? kNoColumnSizing;
  const handleColumnSizingChange = useCallback(
    (next: Record<string, number>) => patchView({ columnWidths: next }),
    [patchView]
  );

  // Score column visibility comes from `selectedScores` (so toggling a
  // scorer in the column popover stays consistent with the rest of the
  // app that reads `selectedScores`). Non-score columns come from the
  // descriptor-projected `columnVisibility` map.
  const selectedScoreFields = useMemo(
    () => new Set(selectedScores.map(perScorerFieldKey)),
    [selectedScores]
  );

  // Controlled visibility map keyed by column id, consumed by the DataGrid.
  const visibilityForGrid = useMemo<Record<string, boolean>>(() => {
    const v: Record<string, boolean> = {};
    for (const col of allColumns) {
      const key = col.id ?? "";
      const seeded = columnVisibility[key];
      v[key] = seeded === undefined ? true : seeded;
    }
    for (const label of scores) {
      const id = perScorerFieldKey(label);
      v[id] = selectedScoreFields.has(id);
    }
    return v;
  }, [allColumns, columnVisibility, scores, selectedScoreFields]);

  const allScoreFields = useMemo(
    () => new Set(scores.map(perScorerFieldKey)),
    [scores]
  );

  // When the user toggles columns in the popover, split score-column
  // changes back into `selectedScores` and persist the rest as normal
  // column visibility.
  const handleVisibilityChange = useCallback(
    (next: Record<string, boolean>) => {
      const newSelected = scores.filter(
        (label) => next[perScorerFieldKey(label)] !== false
      );
      const sameAsBefore =
        newSelected.length === selectedScores.length &&
        newSelected.every((s, i) => {
          const cur = selectedScores[i];
          return cur && cur.scorer === s.scorer && cur.name === s.name;
        });
      if (!sameAsBefore) setSelectedScores(newSelected);

      const nonScoreVisibility: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(next)) {
        if (!allScoreFields.has(key)) nonScoreVisibility[key] = value;
      }
      setColumnVisibility(nonScoreVisibility);
    },
    [
      scores,
      selectedScores,
      setSelectedScores,
      setColumnVisibility,
      allScoreFields,
    ]
  );

  // Build SampleRow items from filtered sample summaries.
  const items: SampleRow[] = useMemo(() => {
    if (!samplesDescriptor || !selectedLogFile) return [];
    return sampleSummaries.map((sample): SampleRow => {
      const tokens = totalSampleTokens(sample.model_usage);
      return {
        logFile: selectedLogFile,
        sampleId: sample.id,
        epoch: sample.epoch,
        data: sample,
        answer:
          samplesDescriptor.selectedScorerDescriptor(sample)?.answer() ?? "",
        completed: sample.completed,
        input: inputString(sample.input).join(" "),
        target: Array.isArray(sample.target)
          ? sample.target.join(", ")
          : sample.target,
        error: sample.error,
        limit: sample.limit,
        limit_reason: sample.limit_reason,
        retries: sample.retries,
        fallbacks: totalModelFallbacks(sample.model_fallbacks) || undefined,
        tokens,
        cost: totalSampleCost(sample.model_usage),
        duration: sample.total_time ?? undefined,
      };
    });
  }, [sampleSummaries, samplesDescriptor, selectedLogFile]);

  if (summariesState.error) {
    return (
      <ErrorPanel
        title="An error occurred while loading samples."
        error={summariesState.error}
      />
    );
  }

  if (totalSampleCount === 0) {
    if (running) {
      return <RunningNoSamples />;
    } else if (summariesState.loading) {
      // Not "No samples" yet — the summaries haven't settled.
      return null;
    } else {
      return <NoContentsPanel text="No samples" />;
    }
  }

  const inlineDisplay = samplesDescriptor && totalSampleCount === 1;
  const listDisplay = samplesDescriptor && totalSampleCount > 1;

  return (
    <Fragment>
      {inlineDisplay ? <InlineSampleDisplay scrollRef={scrollRef} /> : null}
      {listDisplay ? (
        <SampleList
          items={items}
          columns={orderedColumns}
          columnVisibility={visibilityForGrid}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnSizing={columnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          earlyStopping={selectedLogDetails?.results?.early_stopping}
          totalItemCount={evalSampleCount}
          running={running}
          multiline={view.multiline}
          scrollRef={scrollRef}
          columnFilters={columnFilterSpecs ?? kNoColumnFilters}
          onColumnFilterChange={handleColumnFilterChange}
          hideColumnFilters={columnFilterSpecs === null}
          selectableSamples={canExportSamples ? summariesState.data : undefined}
        />
      ) : null}
      {listDisplay ? (
        <ColumnSelectorPopover
          showing={showColumnSelector}
          setShowing={setShowColumnSelector}
          columns={pickerColumns}
          visibility={visibilityForGrid}
          onVisibilityChange={handleVisibilityChange}
          positionEl={columnButtonEl}
          scoresHeading="Scores"
          onResetToDefault={resetColumns}
        />
      ) : null}
    </Fragment>
  );
};
